import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { SUPPORTED_LANGUAGES } from './languages';

const RTL_CODES = new Set(['ar', 'fa', 'he', 'ur', 'ps', 'sd', 'ug', 'yi', 'ks']);

export interface I18nOptions {
  /**
   * Bundled translation objects per language code (e.g. en, de, pt-BR).
   * When set, all strings load from the app bundle (no runtime HTTP fetch for locale files).
   */
  resources: Record<string, { translation: Record<string, unknown> }>;
}

let initPromise: Promise<void> | null = null;

function applyDirection(lng: string) {
  const base = lng.split('-')[0];
  const dir = RTL_CODES.has(base) ? 'rtl' : 'ltr';
  if (typeof document !== 'undefined' && document.documentElement) {
    document.documentElement.dir = dir;
  }
}

/** Build i18next resources from Vite import.meta.glob (eager) on per-locale translation.json files. */
export function buildResourcesFromGlob(
  modules: Record<string, { default: Record<string, unknown> }>
): Record<string, { translation: Record<string, unknown> }> {
  const resources: Record<string, { translation: Record<string, unknown> }> = {};
  for (const p of Object.keys(modules)) {
    const match = p.match(/locales[/\\]([^/\\]+)[/\\]translation\.json$/);
    if (!match) continue;
    resources[match[1]] = { translation: modules[p].default };
  }
  return resources;
}

/**
 * Wait until createI18n()'s i18next.init() has finished.
 * Platforms that lazy-load bundles via addResourceBundle must await this
 * first — otherwise a deferred init with empty `resources` can wipe them.
 */
export function whenI18nReady(): Promise<void> {
  if (i18n.isInitialized) return Promise.resolve();
  return initPromise ?? Promise.resolve();
}

export function createI18n(options: I18nOptions) {
  const { resources } = options;
  const supportedLngs = SUPPORTED_LANGUAGES.map((l) => l.code);

  if (!i18n.isInitialized && !initPromise) {
    initPromise = i18n
      .use(LanguageDetector)
      .use(initReactI18next)
      .init({
        resources,
        fallbackLng: 'en',
        supportedLngs,
        nonExplicitSupportedLngs: true,
        load: 'languageOnly',
        detection: {
          order: ['localStorage', 'navigator', 'htmlTag'],
          lookupLocalStorage: 'platzona_language',
          caches: ['localStorage'],
          convertDetectedLanguage: (lng: string) => lng.split('-')[0],
        },
        ns: ['translation'],
        defaultNS: 'translation',
        interpolation: { escapeValue: false },
        react: { useSuspense: true },
      })
      .then(() => {
        applyDirection(i18n.language || 'en');
        i18n.on('languageChanged', applyDirection);
      })
      .then(() => undefined);

    // Best-effort sync direction if init already completed inline.
    if (i18n.isInitialized) {
      applyDirection(i18n.language || 'en');
    }
  }

  return i18n;
}

export default i18n;
